<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="連結先データ構成追加">
        <v-btn
            icon
            v-if="isEditable"
            @click="openCreateDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>
      <div class="detail-table-header" :style="{left: leftTableWidth}">
        <span style="color: rgba(255,255,255,0.9); margin-right: 10px;">連結キー</span>
        <template v-if="selectedRelation">
          <span style="font-weight: bold;">{{ selectedRelation.targetDataComponentModel.name }}</span>
          <span style="margin-left: 5px;" v-if="selectedRelation.name">({{ selectedRelation.name }})</span>
        </template>
      </div>
    </template>
    <multipane @paneResize="handleMultipaneMove" @paneResizeStop="handleMultipaneMove">

      <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
             連結先データ構成モデル一覧テーブル
       - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
      <el-table
          ref="relationTable"
          :data="dataRelations"
          :style="{width: leftTableWidth, minWidth: '400px'}"
          :height="tabItemBounds.height"
          class="target-data-component-model-table"
          row-key="id"
          border
          highlight-current-row
          @current-change="handleChangeSelectedDataRelation">
        <el-table-column
            prop="targetDataComponentModel.name"
            label="連結先データ構成モデル">
        </el-table-column>
        <el-table-column
            min-width="70"
            label="名称"
            key="name">
          <template #default="{row}">
            <table-inline>
              <v-text-field
                  v-model="row.name"
                  :readonly="!isEditable"
                  @input="handleChangeDataRelation(row)">
              </v-text-field>
            </table-inline>
          </template>
        </el-table-column>
        <el-table-column
            v-if="isEditable"
            width="60"
            align="right">
          <template #default="{row}">
            <icon-btns>
              <icon-btn
                  type="delete"
                  @click="openDeleteDialog(row)">
              </icon-btn>
            </icon-btns>
          </template>
        </el-table-column>
      </el-table>
      <multipane-resizer></multipane-resizer>

      <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
             詳細テーブル
       - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
      <el-table
          border
          :default-sort="{prop: 'sourceProperty.index'}"
          :data="dataPropertyRelations"
          :cell-class-name="cellClassName"
          :height="tabItemBounds.height">
        <el-table-column
            width="50"
            align="center"
            prop="sourceProperty.index"
            label="No">
          <template #default="{row}">
            <el-tooltip v-if="row.sourceProperty.isPrimaryKey" placement="bottom" content="主キー">
              <span>{{ row.sourceProperty.index }}</span>
            </el-tooltip>
            <span v-else>{{ row.sourceProperty.index }}</span>
          </template>
        </el-table-column>
        <el-table-column
            prop="sourceProperty.name"
            label="連結元データ項目定義">
          <template #default="{row}">
            <data-property-definition-descriptor-popover :value="row.sourceProperty">
              <span>{{ row.sourceProperty.name }}</span>
            </data-property-definition-descriptor-popover>
          </template>
        </el-table-column>
        <el-table-column
            prop="targetProperty.name"
            class-name="no-padding target-property"
            label="連結先データ項目定義">
          <template #default="{row}">
            <table-inline>
              <v-select
                  :value="row.targetProperty"
                  :items="targetDataProperties"
                  item-text="name"
                  item-value="id"
                  :clearable="isEditable"
                  :readonly="!isEditable"
                  @change="handleChangeTargetPropertyRelation(row, $event)">
                <template #selection="slot">
                  <data-property-definition-descriptor-popover :value="row.targetProperty"
                                                               :id="`${row.sourceProperty.index}`">
                    <span v-show="slot.item.id !== -1">{{ slot.item.name }}</span>
                  </data-property-definition-descriptor-popover>
                </template>
                <template #item="{item}">
                  <data-property-definition-descriptor-popover :value="item">
                    <div
                        :class="{'target-property-item': true, 'primary-key': item.isPrimaryKey, undefined: item.id === -1}">
                      <div class="index">
                        {{ item.index }}
                      </div>
                      <div class="name">
                        {{ item.name }}
                      </div>
                    </div>
                  </data-property-definition-descriptor-popover>
                </template>
              </v-select>
            </table-inline>
          </template>
        </el-table-column>
      </el-table>
    </multipane>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
           データ連結モデル作成ダイアログ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->

    <simple-dialog
        v-model="dialogCreate"
        title-text="データ連結モデル作成"
        @close="closeCreateDialog">
      <v-row :set="dcms = entities.dataComponentModel.find()">
        <v-col cols="12">
          <model-select
              v-model="dialogCreate.targetDataComponentModelId"
              label="連結先データ構成モデル"
              :items="dcms"
              :groups="entities.category.find()"
              item-group="categoryIds"
              :rules="[v => (!!v || v === 0) || '連結先データ構成モデルを設定してください']"
              autofocus
              default-expand-all
              taller>
            <template #item="{item, on, attrs}">
              <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover dictionary-menu-item">
                <v-col class="name">
                  <menu-inline>
                    <text-field-like
                        label="名称"
                        :value="item.name"
                        :value-hds-id="item.id">
                    </text-field-like>
                  </menu-inline>
                </v-col>
                <v-col>
                  <menu-inline>
                    <text-field-like
                        label="説明"
                        :value="item.description"
                        placeholder="未設定"
                        multi-line>
                    </text-field-like>
                  </menu-inline>
                </v-col>
              </v-list-item>
            </template>
          </model-select>
        </v-col>
        <v-col cols="12" :set="selectedDcm = dcms.find(dcm => dcm.id === dialogCreate.targetDataComponentModelId)">
          <v-text-field
              v-model="dialogCreate.name"
              messages="わかりやすい名前をつけておくのがお勧めです。後で変更が可能です"
              label="名称"/>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
           データ連結モデル削除ダイアログ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->

    <simple-dialog
        v-model="dialogDelete"
        title-text="データ連結モデル削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のデータ連結モデルを削除します。本当によろしいですか？"
        @close="closeDeleteDialog">
      <v-row>
        <v-col cols="12">
          <model-select
              v-model="dialogDelete.targetDataComponentModelId"
              label="連結先データ構成モデル"
              :items="entities.dataComponentModel.find()"
              readonly>
          </model-select>
        </v-col>
        <v-col cols="12">
          <v-text-field
              v-model="dialogDelete.name"
              label="名称"
              readonly/>
        </v-col>
      </v-row>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import natsort from "natsort";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import DataPropertyDefinitionDescriptorPopover
  from "@/components/pages/dictionary/DataComponentModel/DataPropertyDefinitionDescriptorPopover.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import mutationDataRelationModelAdd from "@/gql/DataComponentModel/DataRelationModelAdd.gql";
import mutationDataRelationModelRemove from "@/gql/DataComponentModel/DataRelationModelRemove.gql";
import {Multipane, MultipaneResizer} from "vue-multipane";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import MenuInline from "@/components/atoms/MenuInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

const sortFn = natsort();

export default {
  name: 'data-relation-list',
  components: {
    TextFieldLike,
    MenuInline,
    IconBtn,
    IconBtns,
    TabItemTemplate,
    TableInline, ModelSelect, SimpleDialog, DataPropertyDefinitionDescriptorPopover,
    Multipane,
    MultipaneResizer
  },
  mixins: [PathInfoMixin],
  props: {
    dictionary: {
      type: Object,
      required: true
    },
    dataComponentModel: {
      type: Object,
      required: true
    }
  },
  inject: [
    "tabItemBounds"
  ],
  data() {
    return {
      selectedRelation: null,
      leftTableWidth: '40%',
      dialogDelete: false,
      dialogCreate: false,
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities',
    }),
    dataRelations() {
      return this.entities.dataRelation
          .find({
            sourceDataComponentModelId: this.dataComponentModel.id
          })
          .map(r => {
            const targetDataComponentModel = this.entities.dataComponentModel.findOne({
              id: r.targetDataComponentModelId
            });
            return {...r, targetDataComponentModel};
          })
          .sort((a, b) => sortFn(a.name, b.name));
    },
    dataPropertyRelations() {
      const _e = this.entities;
      let result = _e.dataPropertyDefinition
          .find({dataComponentModelId: this.dataComponentModel.id})
          .map(r => {
            let targetProperty = null;
            let propertyRelation = null;
            if (this.selectedRelation) {
              propertyRelation = _e.dataPropertyRelation.findOne({
                dataRelationModelId: this.selectedRelation.id,
                sourceDataPropertyDefinitionId: r.id
              });
              if (propertyRelation) {
                targetProperty = _e.dataPropertyDefinition.findOne({
                  id: propertyRelation.targetDataPropertyDefinitionId
                });
              }
            }
            if (!targetProperty) {
              targetProperty = {dataPropertyDefinitionId: -1};
            }
            return {sourceProperty: r, targetProperty, propertyRelation};
          });
      if (!this.isEditable) {
        result = result.filter(r => r.targetProperty.dataPropertyDefinitionId !== -1)
      }
      return result;
    },
    targetDataProperties() {
      if (this.selectedRelation) {
        return this.entities.dataPropertyDefinition
            .find({dataComponentModelId: this.selectedRelation.targetDataComponentModelId})
            .sort((a, b) => a.index - b.index);
      } else {
        return [];
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      const relations = this.dataRelations;
      if (relations.length) {
        this.$refs.relationTable.setCurrentRow(relations[0]);
      }
    });
  },
  methods: {
    ...mapMutations({
      deleteDataRelation: 'hds/deleteDataRelation',
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    handleChangeDataRelation(dataRelation) {
      const _e = this.entities;
      _e.dataRelation.update(dataRelation);
      this.$emit('dirty');
    },
    handleChangeSelectedDataRelation(selectedRelation) {
      this.selectedRelation = selectedRelation;
    },
    handleChangeTargetPropertyRelation(propertyRelationRow, targetPropertyId) {
      if (!this.selectedRelation) {
        return;
      }
      let dirty = false;
      const _e = this.entities;
      if (propertyRelationRow.propertyRelation) {
        _e.dataPropertyRelation.remove(propertyRelationRow.propertyRelation);
        dirty = true;
      }
      if (targetPropertyId !== -1 && targetPropertyId !== undefined) {
        _e.dataPropertyRelation.insert({
          dataRelationModelId: this.selectedRelation.id,
          sourceDataPropertyDefinitionId: propertyRelationRow.sourceProperty.id,
          targetDataPropertyDefinitionId: targetPropertyId
        });
        dirty = true;
      }
      if (dirty) {
        _e.dataRelation.update(this.selectedRelation);
        this.$emit('dirty');
      }
    },
    handleMultipaneMove(pane, resizer, size) {
      this.leftTableWidth = `${pane.clientWidth}px`;
    },
    cellClassName({row, column}) {
      if (column.property === 'sourceProperty.index' && row.sourceProperty.isPrimaryKey) {
        return 'primary-key';
      }
      return '';
    },

    openCreateDialog() {
      this.dialogCreate = {
        name: '',
        sourceDataComponentModelId: this.dataComponentModel.id,
        targetDataComponentModelId: null,
        dataPropertyRelationModels: []
      };
    },
    closeCreateDialog(apply) {
      if (apply) {
        const newEntity = this.dialogCreate;
        this.showSnackbar({
          message: `データ連結モデル「${newEntity.name}」を作成中...`,
          timeout: false,
          loading: true
        });
        this.gqlMutateAndLoad({
              mutation: mutationDataRelationModelAdd,
              variables: {
                addDataRelationModels: [newEntity]
              },
              mapping: {
                dataRelation: 'dataRelationModels',
              }
            })
            .then(data => {
              this.showSnackbar({
                message: `データ連結モデル「${newEntity.name}」を作成しました`
              });
              const selectedRelation = this.entities.dataRelation.findOne({id: data.dataRelationModels[0].id});
              const targetDataComponentModel = this.entities.dataComponentModel.findOne({
                id: selectedRelation.targetDataComponentModelId
              });
              this.selectedRelation = {
                ...selectedRelation,
                targetDataComponentModel
              }
              this.dialogCreate = false;
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `データ連結モデル「${newEntity.name}」の作成に失敗しました`
              });
            });
      } else {
        this.dialogCreate = false;
      }
    },

    openDeleteDialog(row) {
      this.dialogDelete = row;
    },
    closeDeleteDialog(apply) {
      const _e = this.entities;
      if (apply) {
        const removeEntity = this.dialogDelete;
        this.showSnackbar({
          message: `データ連結モデル「${removeEntity.name}」削除中...`,
          timeout: false,
          loading: true
        });
        this.gqlMutate({
              mutation: mutationDataRelationModelRemove,
              variables: {
                removeDataRelationModelIds: [removeEntity.id]
              }
            })
            .then(data => {
              _e.dataRelation.remove(removeEntity);
              this.showSnackbar({
                message: `データ連結モデル「${removeEntity.name}」が削除されました`
              });
              this.selectedRelation = null;
              this.dialogDelete = false;
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `データ連結モデル「${removeEntity.name}」の削除に失敗しました`
              });
            });
      } else {
        this.dialogDelete = false;
      }
    },
  },

  watch: {
    dataRelations() {
      this.$nextTick(() => {
        if (this.selectedRelation) {
          this.$refs.relationTable.setCurrentRow(this.selectedRelation);
        }
      });
    }
  }
}

</script>

<style lang="scss" scoped>
@import "src/theme";

.detail-table-header {
  position: absolute;
  padding-left: 10px;
  white-space: pre;
  overflow: hidden;
}

::v-deep {
  .el-table__row > .primary-key {
    @extend .primary-key-base;
  }

  .target-property {
    padding: 0;

    .v-select {
      font-size: 14px;
    }
  }
}

// 連結先データ項目定義の選択肢のデータ項目定義
.target-property-item {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  line-height: 40px;
  border-bottom: 1px solid #afafaf;

  .index {
    text-align: center;
    width: 40px;
    border-right: 1px solid #afafaf;
  }

  &.primary-key .index {
    @extend .primary-key-base;
    border-right: 1px solid $primaryKeyColor;
  }

  &.undefined .index {
    border-right: 1px none;
  }

  .name {
    flex-grow: 1;
  }
}

// 「無指定」の色
.undefined {
  color: gray;
}
</style>
